<template>
  <div class="hypervisor-migrate-results">
    <div v-if="loading" class="hypervisor-migrate-results-overlay">
      <div class="hypervisor-migrate-results-loader">
        <AppSpinnerIcon size="md" class="hypervisor-migrate-results-spinner" />
        <span>Loading migration targets...</span>
      </div>
    </div>
    <div class="vm-detail-shell" :id="shellId">
      <section v-for="group in groups" :key="`hv-group-${group.id}`" class="hypervisor-migrate-group">
        <table class="table table-striped table-bordered table-condensed">
          <thead>
            <tr>
              <th><div class="text-center">HyperVisor Name</div></th>
              <th><div class="text-center">Status</div></th>
              <th><div class="text-center">CPU</div></th>
              <th><div class="text-center">Type</div></th>
              <th class="hidden-tablet"><div class="text-center">IP address</div></th>
              <th><div class="text-center">Running VMs</div></th>
              <th class="hidden-tablet"><div class="text-center">Description</div></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><div class="text-center">{{ group.name }}</div></td>
              <td class="text-center"><span class="app-status-chip" :class="group.status_class">{{ group.status_label }}</span></td>
              <td><div class="text-center">{{ group.cpu_name }}</div></td>
              <td><div class="text-center">{{ group.hv_type_name }}</div></td>
              <td class="hidden-tablet"><div class="text-center">{{ group.ipaddress }}</div></td>
              <td>
                <div class="text-center">
                  <span :id="`hypervisor-migrate-count-${group.id}`">{{ group.vm_count_value }}</span> (libvirt : {{ group.libvirt_vm_count }})
                </div>
              </td>
              <td class="hidden-tablet"><div class="text-center">{{ group.description }}</div></td>
            </tr>
          </tbody>
        </table>

        <table class="table table-striped table-bordered table-condensed vm-detail-table-wrap">
          <thead>
            <tr>
              <th><div class="text-center">Name</div></th>
              <th><div class="text-center">Status</div></th>
              <th class="hidden-tablet"><div class="text-center">CPU</div></th>
              <th class="hidden-tablet"><div class="text-center">Memory</div></th>
              <th class="hidden-tablet"><div class="text-center">Boot</div></th>
              <th class="hidden-tablet"><div class="text-center">Network</div></th>
              <th><div class="text-center">Owner</div></th>
              <th style="width: 270px"><div class="text-center">Destination of Migration</div></th>
            </tr>
          </thead>
          <tbody :id="`hypervisor-migrate-body-${group.id}`">
            <tr v-for="row in group.rows || []" :id="row.row_id" :key="`migrate-row-${row.id}`">
              <td>
                <a :href="row.detail_href" @click.prevent.stop="handleOpenVmDetail(row.detail_url)">
                  <AppOsIcon :symbol="row.os_icon_symbol" />
                  {{ row.name }}
                </a>
              </td>
              <td class="text-center">
                <span class="app-status-chip" :class="row.state_class">{{ row.state_label }}</span>
              </td>
              <td class="hidden-tablet"><div class="text-center">{{ row.vcpu }}</div></td>
              <td class="hidden-tablet"><div class="text-center">{{ row.mem_label }}</div></td>
              <td class="hidden-tablet"><div class="text-center">{{ row.boot_label }}</div></td>
              <td class="hidden-tablet"><div class="text-center">{{ row.network_name }}</div></td>
              <td><div class="text-center">{{ row.owner }}</div></td>
              <td v-if="!row.can_migrate" class="unknown">
                {{ row.migrate_block_reason }}
              </td>
              <td v-else>
                <form :action="row.form_action" method="post" class="vm-hv-migrate-form" @submit.prevent="handleMigrate(row, $event)">
                  <div class="vm-inline-select-bar">
                    <div class="vm-search-select vm-search-select-inline" data-vue-managed="true">
                      <OsTypeSelect
                        input-name="migrate[dest]"
                        :options="destinationOptions(row)"
                        :initial-value="row.selected_destination_id || ''"
                        empty-label="Select a destination hypervisor"
                        filter-placeholder="Filter hypervisors"
                      />
                    </div>
                    <button
                      :id="row.submit_id"
                      type="submit"
                      class="btn btn-sm btn-outline-primary app-action-btn app-action-btn--primary app-action-btn--icon"
                      title="Migrate VM"
                      aria-label="Migrate VM"
                    >
                      <AppIcon name="migrate" />
                    </button>
                    <div :id="row.spinner_id" class="hypervisor-migrate-inline-spinner" style="display:none">
                      <span class="hypervisor-migrate-inline-badge">
                        <AppSpinnerIcon size="sm" class="hypervisor-migrate-results-spinner" />
                        <span>Migrating</span>
                      </span>
                    </div>
                  </div>
                </form>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <aside
        :id="`${shellId}-panel`"
        class="vm-detail-panel"
        :style="vmDetailPanelStyle"
        :aria-hidden="vmDetail.open && vmDetail.mode === 'panel' ? 'false' : 'true'"
      >
        <VmDetailPanel
          :open="vmDetail.open && vmDetail.mode === 'panel'"
          :title="vmDetail.title"
          :detail-data="vmDetail.detailData"
          :ip-refresh-url="vmDetail.ipRefreshUrl"
          @close="closeVmDetail"
        />
      </aside>
      <VmDetailModal
        :modal-id="vmDetailModalId"
        :open="vmDetail.open && vmDetail.mode === 'modal'"
        :title="vmDetail.title"
        :detail-data="vmDetail.detailData"
        :ip-refresh-url="vmDetail.ipRefreshUrl"
        @close="closeVmDetail"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import AppIcon from './AppIcon.vue'
import AppOsIcon from './AppOsIcon.vue'
import AppSpinnerIcon from './AppSpinnerIcon.vue'
import OsTypeSelect from './OsTypeSelect.vue'
import VmDetailModal from './VmDetailModal.vue'
import VmDetailPanel from './VmDetailPanel.vue'

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({ groups: [] })
  }
})

const groups = ref(props.initialData?.groups || [])
const loading = ref(!groups.value.length)
const shellId = ref('hypervisor-migrate-vm-detail-shell')
const vmDetail = ref({
  open: false,
  mode: 'panel',
  title: '',
  detailData: null,
  ipRefreshUrl: ''
})
const vmDetailPanelStyle = ref({})
const vmDetailModalId = computed(() => `show_hv_vm_detail_${shellId.value}`)

function sccShowToast(level, message) {
  window.sccStatusShell?.sccShowToast?.(level, message)
}

function handleUpdate(event) {
  groups.value = event.detail?.groups || []
  shellId.value = event.detail?.shell_id || groups.value?.[0]?.rows?.[0]?.shell_id || 'hypervisor-migrate-vm-detail-shell'
  window.setTimeout(() => {
    if (window.sccMountVueComponents) {
      window.sccMountVueComponents()
    }
  }, 0)
}

function handleLoading(event) {
  loading.value = !!event.detail?.loading
}

function selectedDestinationLabel(row) {
  const selected = (row.candidate_destinations || []).find((destination) => {
    return String(destination.id) === String(row.selected_destination_id || '')
  })

  return selected?.name || 'Select a destination hypervisor'
}

function destinationOptions(row) {
  return (row.candidate_destinations || []).map((destination) => ({
    label: destination.name,
    value: destination.id
  }))
}

function handleOpenVmDetail(url) {
  if (!url) {
    return
  }

  requestJson(url, { method: 'GET' })
    .then((payload) => {
      const usePanel = shouldUseVmDetailPanel()
      const mode = usePanel ? 'panel' : 'modal'
      vmDetail.value = {
        open: true,
        mode,
        title: payload?.title || '',
        detailData: payload?.detail_data || null,
        ipRefreshUrl: payload?.ip_refresh_url || ''
      }

      const shell = document.getElementById(shellId.value)
      if (shell) {
        if (mode === 'panel') {
          shell.classList.add('vm-detail-shell-open')
          layoutVmDetailPanel()
        } else {
          shell.classList.remove('vm-detail-shell-open')
        }
      }
    })
    .catch((error) => {
      sccShowToast('danger', error?.message || 'Failed to load VM detail.')
    })
}

function shouldUseVmDetailPanel() {
  const shell = document.getElementById(shellId.value)
  const panel = document.getElementById(`${shellId.value}-panel`)
  if (!shell || !panel || window.innerWidth <= 991) {
    return false
  }

  const shellRect = shell.getBoundingClientRect()
  const availableRight = Math.max(0, window.innerWidth - shellRect.right - 18)
  return availableRight >= 280
}

function layoutVmDetailPanel() {
  const shell = document.getElementById(shellId.value)
  const table = shell ? shell.querySelector('.vm-detail-table-wrap') : null
  const footer = document.querySelector('.footer')
  const gap = 18
  if (!shell) {
    return
  }

  const shellRect = shell.getBoundingClientRect()
  const shellWidth = shell.offsetWidth
  const footerHeight = footer ? footer.offsetHeight : 0
  const viewportAvailableHeight = Math.max(320, window.innerHeight - Math.max(shellRect.top, 0) - footerHeight - gap)
  const panelHeight = Math.max(table ? table.offsetHeight : shell.offsetHeight, viewportAvailableHeight)

  if (window.innerWidth <= 991) {
    vmDetailPanelStyle.value = {
      left: '0px',
      width: `${shellWidth}px`,
      minHeight: `${panelHeight}px`,
      height: `${panelHeight}px`
    }
    return
  }

  const minPreferredWidth = 400
  const maxPreferredWidth = 560
  const availableRight = Math.max(0, window.innerWidth - shellRect.right - gap)
  const preferredWidth = Math.min(maxPreferredWidth, Math.max(minPreferredWidth, availableRight))
  let left = shellWidth + gap

  if (availableRight < preferredWidth) {
    left = Math.max(24, left - (preferredWidth - availableRight))
  }

  vmDetailPanelStyle.value = {
    left: `${left}px`,
    width: `${preferredWidth}px`,
    minHeight: `${panelHeight}px`,
    height: `${panelHeight}px`
  }
}

function closeVmDetail() {
  const shell = document.getElementById(shellId.value)
  if (shell) {
    shell.classList.remove('vm-detail-shell-open')
  }

  vmDetail.value = {
    open: false,
    mode: vmDetail.value.mode,
    title: '',
    detailData: null,
    ipRefreshUrl: ''
  }
}

function rerenderVmDetailForViewport() {
  if (!vmDetail.value.open) {
    return
  }

  const usePanel = shouldUseVmDetailPanel()
  const mode = usePanel ? 'panel' : 'modal'
  vmDetail.value = {
    ...vmDetail.value,
    mode
  }

  const shell = document.getElementById(shellId.value)
  if (shell) {
    if (mode === 'panel') {
      shell.classList.add('vm-detail-shell-open')
      layoutVmDetailPanel()
    } else {
      shell.classList.remove('vm-detail-shell-open')
    }
  }
}

async function handleMigrate(row, event) {
  const form = event?.currentTarget
  if (!form) {
    return
  }

  const button = form.querySelector("button[type='submit']")
  const spinner = form.querySelector(`[id='${row.spinner_id}']`)
  const formData = new URLSearchParams(new FormData(form)).toString()

  if (button) button.style.display = 'none'
  if (spinner) spinner.style.display = ''

  try {
    const payload = await requestJson(form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
      }
    })

    if (button) button.style.display = ''
    if (spinner) spinner.style.display = 'none'

    sccShowToast('success', payload?.message || 'Migration Completed.')
    document.dispatchEvent(new CustomEvent('scc:hypervisor-migrate:refresh'))
  } catch (error) {
    if (button) button.style.display = ''
    if (spinner) spinner.style.display = 'none'

    sccShowToast('danger', error.message || 'Migration failed.')
  }
}

async function requestJson(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase()
  const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute('content') || ''
  const headers = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(options.headers || {})
  }
  const fetchOptions = {
    method,
    headers,
    credentials: 'same-origin'
  }

  if (csrfToken && method !== 'GET' && method !== 'HEAD') {
    headers['X-CSRF-Token'] = csrfToken
  }

  if (options.body) {
    fetchOptions.body = options.body
  }

  const response = await fetch(url, fetchOptions)
  const text = await response.text()
  let payload = null

  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!response.ok) {
    throw new Error((payload && payload.message) || text || 'Request failed.')
  }

  return payload
}

onMounted(() => {
  shellId.value = groups.value?.[0]?.rows?.[0]?.shell_id || 'hypervisor-migrate-vm-detail-shell'
  document.addEventListener('scc:hypervisor-migrate-results:update', handleUpdate)
  document.addEventListener('scc:hypervisor-migrate-results:loading', handleLoading)
  document.addEventListener('keyup', handleDocumentKeyup)
  window.addEventListener('resize', rerenderVmDetailForViewport)
  window.setTimeout(() => {
    if (window.sccMountVueComponents) {
      window.sccMountVueComponents()
    }
  }, 0)
})

onBeforeUnmount(() => {
  document.removeEventListener('scc:hypervisor-migrate-results:update', handleUpdate)
  document.removeEventListener('scc:hypervisor-migrate-results:loading', handleLoading)
  document.removeEventListener('keyup', handleDocumentKeyup)
  window.removeEventListener('resize', rerenderVmDetailForViewport)
})

function handleDocumentKeyup(event) {
  if (event.key !== 'Escape') {
    return
  }

  closeVmDetail()
}
</script>

<style scoped>
.hypervisor-migrate-results {
  position: relative;
  min-height: 120px;
}

.hypervisor-migrate-results-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(248, 250, 253, 0.82);
  backdrop-filter: blur(1px);
}

.hypervisor-migrate-results-loader {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border: 1px solid rgba(130, 150, 175, 0.24);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  color: #36506a;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 16px 34px rgba(122, 141, 164, 0.16);
}

.hypervisor-migrate-results-spinner {
  color: #4d6d8f;
}

.hypervisor-migrate-inline-spinner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.hypervisor-migrate-inline-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.1);
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.hypervisor-migrate-group + .hypervisor-migrate-group {
  margin-top: 24px;
}

</style>
